import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * Searchable multi-select combobox. Visually matches the existing
 * single-select `Combobox` so the admin form feels consistent with
 * the patient/caregiver sign-up flow , just with chips inside the
 * button and a check + persistent click behaviour on each row.
 *
 * Props:
 *   options          : (string | {value, label, icon?})[]
 *   value            : string[]                        (current selection)
 *   onChange         : (next: string[]) => void
 *   placeholder      : string                          (input + empty state)
 *   emptyLabel       : string                          (no-results message)
 *   id, "data-testid": string
 *   error            : boolean                         (red border)
 *   renderChip       : (value) => ReactNode            (optional custom chip)
 *   renderOption     : (option) => ReactNode           (optional custom row)
 */
export function MultiCombobox({
  options = [],
  value = [],
  onChange,
  placeholder = "Search…",
  emptyLabel = "No matches",
  id,
  "data-testid": testId,
  error = false,
  className = "",
  renderChip,
  renderOption,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  // Normalise options into {value, label, icon?}
  const norm = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : o,
      ),
    [options],
  );

  useEffect(() => {
    if (!open) setQuery("");
    else setActive(0);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return norm;
    return norm.filter((o) => o.label.toLowerCase().includes(q));
  }, [norm, query]);

  const toggle = (val) => {
    const set = new Set(value);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    onChange?.([...set]);
  };

  const clearAll = (e) => {
    e?.stopPropagation();
    onChange?.([]);
  };

  const removeOne = (val) => {
    onChange?.(value.filter((v) => v !== val));
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) toggle(filtered[active].value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-i='${active}']`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <div
        id={id}
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        role="button"
        tabIndex={0}
        className={`w-full min-h-12 pl-3 pr-9 py-2 rounded-xl border bg-white text-left flex items-center gap-1.5 flex-wrap cursor-pointer transition-all focus:outline-none focus:ring-4 focus:ring-[var(--lime)]/20 text-[15px] ${
          error
            ? "border-red-300 text-red-700"
            : "border-[var(--light-border)] text-[var(--charcoal)]"
        } ${open ? "border-[var(--lime)] ring-4 ring-[var(--lime)]/20" : ""}`}
      >
        {value.length === 0 && (
          <span className="text-[var(--text-muted)] pl-1">{placeholder}</span>
        )}
        {value.map((v) => {
          const opt = norm.find((o) => o.value === v) || { value: v, label: v };
          return (
            <span
              key={v}
              data-testid={testId ? `${testId}-chip-${v}` : undefined}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--lime)]/15 text-[var(--charcoal)] text-sm font-medium border border-[var(--lime)]/30"
            >
              {renderChip ? renderChip(opt) : <span>{opt.label}</span>}
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  removeOne(v);
                }}
                aria-label={`Remove ${opt.label}`}
                className="p-0.5 rounded hover:bg-white/60"
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        {value.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={clearAll}
            className="ml-auto text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--charcoal)] px-1"
          >
            Clear all
          </button>
        )}
        <ChevronDown
          size={16}
          className={`flex-shrink-0 absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 mt-2 z-30 bg-white rounded-xl border border-[var(--light-border)] shadow-xl overflow-hidden">
          <div className="relative border-b border-[var(--light-border)]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              data-testid={testId ? `${testId}-search` : undefined}
              className="w-full h-10 pl-9 pr-9 text-sm bg-transparent text-[var(--charcoal)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--bone-soft)]"
              >
                <X size={14} className="text-[var(--text-muted)]" />
              </button>
            )}
          </div>

          <ul
            ref={listRef}
            role="listbox"
            aria-multiselectable="true"
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">
                {emptyLabel}
              </li>
            )}

            {filtered.map((o, i) => {
              const selected = value.includes(o.value);
              return (
                <li key={o.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    data-i={i}
                    data-testid={
                      testId ? `${testId}-option-${o.value}` : undefined
                    }
                    onMouseEnter={() => setActive(i)}
                    onClick={() => toggle(o.value)}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${
                      i === active
                        ? "bg-[var(--teal-soft)] text-[var(--teal-dark)]"
                        : "text-[var(--charcoal)]"
                    } ${selected ? "font-medium" : ""}`}
                  >
                    <span className="truncate flex items-center gap-2">
                      {renderOption ? renderOption(o) : o.label}
                    </span>
                    {selected && (
                      <Check size={14} className="text-[var(--teal)] flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MultiCombobox;
